import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { WorshipReport } from '../../types/worshipReport';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { useConfirm } from '../../contexts/ConfirmContext';
import { canEditReport, canDeleteReport } from '../../utils/permissions';

interface WorshipReportTableProps {
  reports: WorshipReport[];
  onDelete: () => void;
  onPreview: (report: WorshipReport) => void;
  onEdit: (report: WorshipReport) => void;
}

export function WorshipReportTable({
  reports, onDelete, onPreview, onEdit }: WorshipReportTableProps) {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [speakerNames, setSpeakerNames] = useState<{[key: string]: string}>({});

  useEffect(() => {
    const fetchSpeakerNames = async () => {
      const speakerIds = [...new Set(reports.map(r => r.speakerId))];
      const names: {[key: string]: string} = {};

      for (const id of speakerIds) {
        if (!id) continue;
        try {
          const speakerRef = collection(db, 'speakers');
          const q = query(speakerRef, where('__name__', '==', id));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            names[id] = querySnapshot.docs[0].data().name;
          }
        } catch (error) {
          console.error('Error fetching speaker:', error);
        }
      }

      setSpeakerNames(names);
    };

    fetchSpeakerNames();
  }, [reports]);

  const handleDelete = async (reportId: string) => {
    const ok = await confirm({ title: 'Supprimer le rapport', description: 'Êtes-vous sûr de vouloir supprimer ce rapport de culte ?', confirmLabel: 'Supprimer', cancelLabel: 'Annuler', variant: 'danger' });
    if (!ok) return;
    
    try {
      await deleteDoc(doc(db, 'worshipReports', reportId));
      onDelete();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Erreur lors de la suppression du rapport');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Thème</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orateur</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nouveaux</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {reports.map((report) => (
            <tr key={report.id} className="hover:bg-green-50/40 transition-colors duration-150 cursor-pointer">
              <td className="px-6 py-4 whitespace-nowrap">
                {new Date(report.date).toLocaleDateString('fr-FR')}
              </td>
              <td className="px-6 py-4 max-w-[260px]"><p className="line-clamp-1 text-sm text-gray-900">{report.messageTheme}</p></td>
              <td className="px-6 py-4">{speakerNames[report.speakerId] || '-'}</td>
              <td className="px-6 py-4">{report.totalParticipants}</td>
              <td className="px-6 py-4">
                {report.totalNewMembers > 0 ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#00665C]/15 text-[#004d45]">
                    +{report.totalNewMembers}
                  </span>
                ) : (
                  <span className="text-gray-400 text-sm">0</span>
                )}
              </td>
              <td className="px-6 py-4">
                <div className="flex space-x-2">
                  <button
                    onClick={() => onPreview(report)}
                    className="text-[#00665C] hover:text-[#004d45] transition-colors"
                    title="Prévisualiser le rapport"
                  >
                    <Eye size={18} />
                  </button>
                  {canEditReport(report, user) && (
                    <button
                      onClick={() => onEdit(report)}
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                      title="Modifier le rapport"
                    >
                      <Pencil size={18} />
                    </button>
                  )}
                  {canDeleteReport(report, user) && (
                    <button
                      onClick={() => handleDelete(report.id!)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                      title="Supprimer le rapport"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}